<template>
  <div class="card">
    <div class="card-body">
      <!-- #region Create and Refresh buttons -->
      <div class="d-flex align-items-center py-2">
        <div class="ms-auto">
          <router-link :to="`form/0`">
            <button class="btn btn-primary me-2 mb-2"><i class="uil uil-plus me-1"></i>Create Adjustment</button>
          </router-link>
          <router-link to="batch-upload/">
            <button class="btn btn-primary me-2 mb-2"><i class="uil uil-plus me-1"></i>Bulk Create Adjustment</button>
          </router-link>
          <button class="btn btn-secondary mb-2" @click="getAdjustments()">
            <feather type="refresh-ccw" size="16" class="icon nav-icon"></feather>&nbsp;&nbsp; Refresh
          </button>
        </div>
      </div>
      <!-- #endregion Create and Refresh buttons -->

      <!-- #region Search and Filter -->
      <div class="row p-2">
        <div class="col-md-4 col-sm-12 p-0">
          <div class="ta-right me-2">
            <b-form-group description="Search Keyword">
              <form class="d-flex" @submit.prevent="">
                <b-form-group id="input-group-3" label-for="search-field">
                  <b-form-select
                    class="form-select rounded-0 rounded-start shadow-none"
                    id="search-field"
                    v-model="search.field"
                    :options="searchFieldOptions"
                  ></b-form-select>
                </b-form-group>

                <b-input-group>
                  <b-form-input class="rounded-0 rounded-end" v-model="search.keyword"></b-form-input>
                </b-input-group>
              </form>
            </b-form-group>
          </div>
        </div>

        <div class="col-md-2 col-sm-12 p-0">
          <div class="ta-right me-2">
            <b-form-group description="Filter by Status">
              <b-form-select
                class="form-select rounded shadow-none dd-custom-size"
                v-model="search.status"
                :options="statusOptions"
              ></b-form-select>
            </b-form-group>
          </div>
        </div>

        <div class="col-md-2 col-sm-12 p-0">
          <div class="ta-right me-2">
            <b-form-group description="Filter by Adjustment Type">
              <b-form-select
                class="form-select rounded shadow-none dd-custom-size"
                v-model="search.adjustmentType"
                :options="adjustmentTypeOptions"
              ></b-form-select>
            </b-form-group>
          </div>
        </div>

        <div class="col-md-2 col-sm-12 p-0">
          <div class="ta-right me-2">
            <b-form-group description="Filter by Adjustment Direction">
              <b-form-select
                class="form-select rounded shadow-none dd-custom-size"
                v-model="search.adjustmentDirection"
                :options="adjustmentDirectionOptions"
              ></b-form-select>
            </b-form-group>
          </div>
        </div>

        <div class="col-md-2 col-sm-12 p-0">
          <div class="ta-right me-2">
            <b-form-group description="Filter by date added" class="me-2 w-daterange">
              <flat-pickr
                v-model="search.dateRange"
                :config="rangeOptions"
                placeholder="Select a date range"
                class="form-control"
              ></flat-pickr>
            </b-form-group>
          </div>
        </div>

        <div class="col-md-6 col-sm-12 d-flex align-items-center">
          <div class="me-2">
            <b-button variant="primary" class="h-fit d-flex align-items-center justify-content-center me-2 w-fit" @click="getAdjustments">
              <i class="bx bx-search me-1 mt-1"></i>Search
            </b-button>
          </div>
          <div class="">
            <b-button
              variant="primary"
              class="h-fit me-2 d-flex align-items-center justify-content-center w-fit"
              @click="
                isSearching = true;
                onExport();
              "
            >
              <i class="uil uil-export me-1"></i>
              Export
            </b-button>
          </div>
        </div>
      </div>
      <!-- #endregion Search and Filter -->

      <!-- #region Table -->
      <div class="table-responsive">
        <div class="table align-middle table-nowrap">
          <b-table
            :items="kankolekAdjustmentsList"
            :fields="fields"
            responsive="sm"
            :current-page="currentPage"
            :sort-by.sync="orderBy"
            :sort-desc.sync="sortDesc"
            :filter-included-fields="filterOn"
            class="table-check"
            @sort-changed="sortList"
          >
            <template #cell(action)="data">
              <!-- <div v-if="checkIfApprover(data.item)">
                <b-button variant="success" class="p-1 me-2" style="background-color: #508f30" @click="updateAdjustment(data.item.id, 'approve')"
                  ><i class="bx bx-check bx-xs bx-fw"></i
                ></b-button>
                <b-button class="p-1 border-0" style="background-color: #6f6f6f; height: 31px; width: 30px" @click="updateAdjustment(data.item.id, 'deny')"
                  ><i class="mdi mdi-close"></i
                ></b-button>
              </div> -->
              <div v-if="data.item.status == 'draft'">
                <router-link :to="{ name: 'Adjustment Transaction Form', params: { id: data.item.id } }" class="tc-white me-2">
                  <b-button variant="primary" class="p-1 mr-2"><i class="bx bx-edit bx-xs bx-fw"></i></b-button>
                </router-link>
                <b-button variant="danger" class="p-1" @click="deleteAdjustment(data.item.id)"><i class="bx bx-trash bx-xs bx-fw"></i></b-button>
              </div>
            </template>

            <template #cell(transaction_id)="data">
              <div>{{ data.item.transaction_id }}</div>
            </template>

            <template #cell(status)="data">
              <div
                :class="{
                  'text-success': data.item.status == 'approved',
                  'text-dark': data.item.status == 'draft',
                  'text-muted': data.item.status == 'denied',
                  'text-danger': data.item.status == 'awaiting_lm' || data.item.status == 'awaiting_fm',
                }"
              >
                {{ setStatus(data.item.status) }}
              </div>
            </template>

            <template #cell(adjustment_direction)="data">
              <div class="text-capitalize">{{ data.item.adjustment_direction }}</div>
            </template>

            <template #cell(adjustment_type)="data">
              <div>{{ data.item.adjustment_type == "reporting_only" ? "Reporting Only" : "Wallet Adjustment" }}</div>
            </template>

            <template #cell(transfer_status)="data">
              <div
                :class="{ 'text-success': data.item.transfer_status == 'success', 
                        'text-danger': data.item.transfer_status == 'pending', 
                        'text-muted': data.item.transfer_status == 'failed' }">
                {{ data.item.transfer_status == "success" ? "Success" : data.item.transfer_status == "pending" ? "Pending" 
                    : data.item.transfer_status == "failed" ? "Failed" : "" }}
              </div>
            </template>

            <template #cell(amount)="data">
              <div>PHP {{ data.item.amount | toCurrency }}</div>
            </template>

            <template #cell(date_rewarded)="data">
              <div v-if="data.item.transfer_status == 'success'">{{ data.item.updated_at | formatDate }}</div>
            </template>
          </b-table>
        </div>
      </div>
      <div class="row g-0 text-center" v-if="kankolekAdjustmentsTotal == 0">
        <span>No data to display.</span>
      </div>
      <!-- #endregion Table -->

      <!-- #region Pagination -->
      <div class="row g-0 text-center text-sm-start" v-if="kankolekAdjustmentsTotal != 0 && kankolekAdjustmentsTotal > 10">
        <div class="col-sm-12">
          <div class="row">
            <div class="col">
              <div class="form-group row pull-left">
                <label for="staticEmail" class="col-sm-6 col-form-label">Display:</label>
                <div class="col-sm-6">
                  <b-form-group label-for="display-record">
                    <b-form-select
                      class="form-select shadow-none w-fit"
                      id="display-record"
                      v-model="perPage"
                      :options="pageOptions"
                      @change="getAdjustments()"
                    ></b-form-select>
                  </b-form-group>
                </div>
              </div>
              <div class="dataTables_paginate paging_simple_numbers float-end" v-if="kankolekAdjustmentsTotal > perPage">
                <b-pagination
                  class="pagination-rounded"
                  v-model="currentPage"
                  @input="getAdjustments()"
                  :total-rows="kankolekAdjustmentsTotal"
                  :per-page="perPage"
                ></b-pagination>
              </div>
            </div>
          </div>
        </div>
        <!-- end col -->
      </div>
      <!-- #endregion Pagination -->
    </div>
  </div>
</template>

<script>
import { layoutMethods, peddlrWalletMethods, peddlrWalletComputed, exportMethods } from "@/state/helpers";
import flatPickr from "vue-flatpickr-component";
import "flatpickr/dist/flatpickr.css";
import Swal from "sweetalert2";

export default {
  page: {
    title: "Kankolek Adjustment Transaction Dashboard",
  },
  data() {
    return {
      currentPage: 1,
      perPage: 10,
      pageOptions: [10, 20, 30, 40, 50, 60, 70, 80, 90, 100],
      filter: null,
      filterOn: [],
      orderBy: "a.id",
      sort: "DESC",
      sortDesc: false,
      submitted: false,
      search: {
        keyword: "",
        field: "",
        status: "",
        adjustmentType: "",
        adjustmentDirection: "",
        dateRange: null,
      },
      rangeOptions: {
        mode: "range",
      },
      searchFieldOptions: [
        { text: "Search All", value: "" },
        { text: "Transaction ID", value: "a.transaction_id" },
        { text: "Reason", value: "a.reason" },
        { text: "Account Number", value: "a.acct_no" },
        { text: "Department", value: "a.requestor_department" },
        { text: "Approved By Line Manager", value: "a.approvedby_line_manager" },
        { text: "Approved By Finance", value: "a.approvedby_finance" },
      ],
      statusOptions: [
        { text: "All", value: "" },
        { text: "Awaiting For Line Manager Approval", value: "awaiting_lm" },
        { text: "Awaiting For Finance Approval", value: "awaiting_fm" },
        { text: "Approved", value: "approved" },
        { text: "Draft", value: "draft" },
        { text: "Declined", value: "denied" },
      ],
      adjustmentTypeOptions: [
        { text: "All", value: "" },
        { text: "Reporting Only", value: "reporting_only" },
        { text: "Wallet Adjustment", value: "wallet_adjustment" },
      ],
      adjustmentDirectionOptions: [
        { text: "All", value: "" },
        { text: "Addition", value: "addition" },
        { text: "Deduction", value: "deduction" },
      ],
      fields: [
        { key: "action", sortable: "" },
        { key: "transaction_id", sortable: true },
        { key: "status", sortable: true },
        { key: "transfer_status", sortable: true },
        { key: "transfer_remarks", sortable: true },
        { key: "adjustment_type", sortable: true },
        { key: "reason", sortable: true },
        { key: "adjustment_direction", sortable: true },
        { key: "amount", sortable: true },
        { key: "acct_no", label: "Account Number", sortable: true },
        { key: "requested_by", sortable: true },
        { key: "requestor_department", label: "Department", sortable: true },
        { key: "approvedby_line_manager", label: "Approved By Line Manager", sortable: true },
        { key: "approvedby_finance", label: "Approved By Finance", sortable: true },
        { key: "date_rewarded", sortable: false },
      ],
      list: [],
    };
  },
  components: {
    flatPickr,
  },
  computed: {
    ...peddlrWalletComputed,
  },
  methods: {
    ...layoutMethods,
    ...exportMethods,
    ...peddlrWalletMethods,
    async getAdjustments() {
      let params = {
        page: this.isSearching ? 1 : this.currentPage,
        rows: this.perPage,
        sort: this.sort,
        orderBy: this.orderBy,
        search: this.search,
      };

      await this.getAdjustmentTransactionList(params).then(() => {
        this.isSearching = false;
      });
    },
    setStatus(status) {
      return status == "awaiting_lm"
        ? "Awaiting For Line Manager Approval"
        : status == "awaiting_fm"
        ? "Awaiting For Finance Approval"
        : status == "approved"
        ? "Approved"
        : status == "denied" ? "Declined" :
        "Draft";
    },
    checkIfApprover(item) {
      let userData = JSON.parse(localStorage.getItem("userData"));
      return userData.email == item.approver_email && item.status != "approved" ? true : false;
    },
    updateAdjustment(id, action) {
      this.adjustmentAction({
        fromERP: true,
        id: id,
        token: null,
        action: action,
      })
        .then(() => {
          Swal.fire({
            title: "Action Successful!",
            text: `Adjustment Transaction has been ${this.action == "approve" ? "approved" : "denied"} successfully.`,
            icon: "success",
            showCancelButton: false,
            confirmButtonColor: "#c4297c",
            confirmButtonText: "OK",
          }).then((result) => {
            if (result.value) {
              this.goToList();
            }
          });
        })
        .catch((error) => {
          Swal.fire({
            title: "Action Failed!",
            html: `Adjustment Transaction has not been ${this.action == "approve" ? "approved" : "denied"}.<br />${error}`,
            icon: "error",
            showCancelButton: false,
            confirmButtonColor: "#c4297c",
            confirmButtonText: "OK",
          }).then((result) => {
            if (result.value) {
              this.goToList();
            }
          });
        });
    },
    async deleteAdjustment(id) {
      Swal.fire({
          title: "Are you sure?",
          text: "Adjustment Transaction will be deleted permanently.",
          icon: "question",
          showCancelButton: true,
          confirmButtonColor: "##804997",
          cancelButtonColor: "#d33",
          confirmButtonText: "Yes",
          cancelButtonText: "No",
        }).then(async (result) => {
          if (result.isConfirmed) {
            await this.actionAdjustmentTransaction({ action: "delete", id: id })
              .then(async () => {
                Swal.fire("Successfully Deleted!", "Adjustment Transaction has been deleted", "success");
                await this.getAdjustments();
              })
              .catch((error) => {
                Swal.fire("Delete Failed!", `An error occured while deleting adjustment transaction: ${error.data.message}`, "error");
              });
          }
        });
    },
    async sortList(action) {
      var column = "";
      switch (action.sortBy) {
        case "transaction_id":
          column = "a.transaction_id";
          break;
        case "status":
          column = "a.status";
          break;
        case "transfer_status":
          column = "a.transfer_status";
          break;
        case "transfer_remarks":
          column = "a.transfer_remarks";
          break;
        case "adjustment_type":
          column = "a.adjustment_type";
          break;
        case "reason":
          column = "a.reason";
          break;
        case "adjustment_direction":
          column = "a.adjustment_direction";
          break;
        case "amount":
          column = "a.amount";
          break;
        case "acct_no":
          column = "a.acct_no";
          break;
        case "requested_by":
          column = "a.requested_by";
          break;
        case "requestor_department":
          column = "a.requestor_department";
          break;
        case "approvedby_line_manager":
          column = "a.approvedby_line_manager";
          break;
        case "approvedby_finance":
          column = "a.approvedby_finance";
          break;
      }

      this.orderBy = column;
      this.sort = action.sortDesc ? "DESC" : "ASC";
      await this.getAdjustments();
    },
    async onExport() {
      let params = {
        module: "adjustment_transactions",
        email: JSON.parse(localStorage.getItem("userData")).email,
        search: this.search,
        sort: this.sort,
        orderBy: this.orderBy,
      };

      this.exportReportExt(params)
        .then(() =>
          Swal.fire(
            "Success!",
            `Your request for report is pending. It will be sent to your email once it's ready.`,
            "success"
          )
        )
        .catch(() =>
          Swal.fire(
            "Failed!",
            `Your request for reports has failed.`,
            "warning"
          )
        );
    },
  },
  async mounted() {
    this.changeTitle({ title: "Adjustment Transactions" });
    this.changeBreadCrumb({
      breadCrumbs: [
        { text: "Peddlr", to: { name: "Home" } },
        { text: "kankolek", to: { name: "" } },
        { text: "Adjustment Transaction", to: { name: "" } },
      ],
    });

    await this.getAdjustments();
  },
};
</script>

<style scoped>
/* .dd-custom-size {
  width: 200px;
}

.w-daterange {
  width: 12.9rem;
} */
</style>
